import React, { useState } from 'react';
import styled from 'styled-components';
import { FiPlus, FiTrash2, FiEdit3 } from 'react-icons/fi';

const TableContainer = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  
  @media (max-width: 768px) {
    border-radius: 6px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
  
  @media (max-width: 768px) {
    min-width: 500px; /* Allow smaller minimum on tablets */
  }
  
  @media (max-width: 480px) {
    min-width: 400px; /* Smaller minimum on phones */
  }
`;

const TableHeader = styled.thead`
  background: #f8fafc;
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  white-space: nowrap;
  min-width: 120px;
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 14px;
    min-width: 100px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 10px;
    font-size: 13px;
    min-width: 80px;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: #f9fafb;
  }
  
  &:hover {
    background: #f3f4f6;
  }
`;

const TableCell = styled.td`
  padding: 8px 16px;
  border-bottom: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  vertical-align: top;
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 8px;
  }
`;

const TableInput = styled.input`
  width: 100%;
  max-width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background: #fefefe;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 16px; /* Prevent zoom on iOS */
    min-height: 44px; /* Better touch target */
  }
  
  @media (max-width: 480px) {
    padding: 8px 10px;
    font-size: 16px;
    min-height: 44px;
  }
  
  &[type="date"] {
    @media (max-width: 768px) {
      padding-right: 36px;
    }
    
    @media (max-width: 480px) {
      padding-right: 32px;
    }
  }
`;

const TableTextarea = styled.textarea`
  width: 100%;
  max-width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background: #fefefe;
  min-height: 60px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;
  overflow-wrap: break-word;
  word-wrap: break-word;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 16px; /* Prevent zoom on iOS */
    min-height: 80px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 10px;
    font-size: 16px;
    min-height: 70px;
  }
`;

const TableSelect = styled.select`
  width: 100%;
  max-width: 100%;
  padding: 8px 32px 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background: #fefefe;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }
  
  @media (max-width: 768px) {
    padding: 10px 32px 10px 12px;
    font-size: 16px; /* Prevent zoom on iOS */
    min-height: 44px; /* Better touch target */
  }
  
  @media (max-width: 480px) {
    padding: 8px 28px 8px 10px;
    font-size: 16px;
    min-height: 44px;
    background-position: right 8px center;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const ActionButton = styled.button`
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  color: #6b7280;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
    color: #374151;
  }

  &.delete:hover {
    background: #fef2f2;
    border-color: #fecaca;
    color: #dc2626;
  }
`;

const AddRowButton = styled.button`
  width: 100%;
  max-width: 100%;
  padding: 12px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  background: #f9fafb;
  color: #6b7280;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
  margin-top: 16px;
  box-sizing: border-box;
  min-height: 48px; /* Better touch target */

  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
    color: #3b82f6;
  }
  
  @media (max-width: 768px) {
    padding: 14px 12px;
    font-size: 16px;
    min-height: 52px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 10px;
    font-size: 15px;
    min-height: 48px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #6b7280;
  background: #f9fafb;
  border-radius: 8px;
  margin: 16px 0;
`;

const TableField = ({ field, value = [], onChange, formData }) => {
  const [tableData, setTableData] = useState(value.length > 0 ? value : [{}]);

  const handleCellChange = (rowIndex, columnKey, cellValue) => {
    const newData = [...tableData];
    if (!newData[rowIndex]) {
      newData[rowIndex] = {};
    }
    newData[rowIndex][columnKey] = cellValue;
    setTableData(newData);
    onChange(newData);
  };

  const addRow = () => {
    const newData = [...tableData, {}];
    setTableData(newData);
    onChange(newData);
  };

  const removeRow = (rowIndex) => {
    if (tableData.length > 1) {
      const newData = tableData.filter((_, index) => index !== rowIndex);
      setTableData(newData);
      onChange(newData);
    }
  };

  const renderCell = (rowIndex, column) => {
    const cellValue = tableData[rowIndex]?.[column.key] || '';
    
    switch (column.type) {
      case 'textarea':
        return (
          <TableTextarea
            value={cellValue}
            onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
            placeholder={column.placeholder || `Enter ${column.label.toLowerCase()}`}
          />
        );
      case 'select':
        return (
          <TableSelect
            value={cellValue}
            onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
          >
            <option value="">Select...</option>
            {column.options?.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </TableSelect>
        );
      case 'number':
        return (
          <TableInput
            type="number"
            value={cellValue}
            onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
            placeholder={column.placeholder || `Enter ${column.label.toLowerCase()}`}
          />
        );
      case 'date':
        return (
          <TableInput
            type="date"
            value={cellValue}
            onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
          />
        );
      default:
        return (
          <TableInput
            type="text"
            value={cellValue}
            onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
            placeholder={column.placeholder || `Enter ${column.label.toLowerCase()}`}
          />
        );
    }
  };

  if (!field.columns || field.columns.length === 0) {
    return (
      <EmptyState>
        <p>No columns configured for this table.</p>
        <p>Please configure the table columns in the form builder.</p>
      </EmptyState>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <tr>
            {field.columns.map((column, index) => (
              <TableHeaderCell key={index}>
                {column.label}
                {column.required && <span style={{ color: '#ef4444' }}> *</span>}
              </TableHeaderCell>
            ))}
            <TableHeaderCell style={{ width: '80px', textAlign: 'center' }}>
              Actions
            </TableHeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {tableData.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {field.columns.map((column, colIndex) => (
                <TableCell key={colIndex}>
                  {renderCell(rowIndex, column)}
                </TableCell>
              ))}
              <TableCell>
                <RowActions>
                  <ActionButton
                    type="button"
                    onClick={() => removeRow(rowIndex)}
                    className="delete"
                    disabled={tableData.length <= 1}
                    title="Remove row"
                  >
                    <FiTrash2 size={12} />
                  </ActionButton>
                </RowActions>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      <AddRowButton type="button" onClick={addRow}>
        <FiPlus size={16} />
        Add Row
      </AddRowButton>
    </TableContainer>
  );
};

export default TableField;
